import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { IndianRupee, PackagePlus, Percent, Wallet } from "lucide-react";

const money = (v) => `₹${Number(v || 0).toFixed(2)}`;
const when = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" }) : "—";

const STATUS_TONE = {
  pending: "border-amber-500/50 text-amber-700 bg-amber-500/10",
  live: "border-primary/40 text-primary bg-primary/10",
  rejected: "border-destructive/40 text-destructive bg-destructive/10",
};

const EMPTY = { name: "", description: "", price: "", stock: "", photo: "" };

const Figure = ({ name, value, icon: Icon, testid }) => (
  <div className="rounded-xl border border-border p-4" data-testid={testid}>
    <div className="overline flex items-center gap-1.5"><Icon size={12} /> {name}</div>
    <div className="font-display text-2xl mt-1">{value}</div>
  </div>
);

export { Figure, money, when };

/**
 * The doctor's product side: what they have put in the shop and what their share of
 * those sales has earned. Kept visually and financially apart from the referral
 * section above it — different money, different ledger, different payout log.
 */
export const PartnerProducts = () => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [share, setShare] = useState({ totals: {}, sales: [], payouts: [] });

  const load = () => {
    api.get("/partner/products").then(({ data }) => setProducts(data.products || [])).catch(() => {});
    api.get("/partner/product-share").then(({ data }) => setShare(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, photo: String(reader.result) }));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/partner/products", {
        name: form.name,
        description: form.description,
        price: parseFloat(form.price) || 0,
        stock: parseInt(form.stock, 10) || 0,
        photo: form.photo || null,
      });
      toast.success("Sent to the clinic for review");
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const totals = share.totals || {};

  return (
    <div className="space-y-6" data-testid="partner-products">
      <div className="pt-4 border-t border-border">
        <p className="overline flex items-center gap-2"><PackagePlus size={14} /> Your products</p>
        <h2 className="font-display text-3xl mt-1">Products and product share</h2>
        <p className="text-sm text-muted-foreground mt-1">
          A separate arrangement from your referral code above: this is your share of sales of the
          products you own, with its own earnings and its own payments.
        </p>
      </div>

      <Card className="border-border">
        <CardContent className="p-6 lg:p-8">
          <h3 className="font-display text-2xl mb-1">Add a product</h3>
          <p className="text-sm text-muted-foreground mb-4">
            The clinic reviews every submission, confirms the price and stock, sets your share for
            that product and then publishes it in the shop.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Product name</Label>
              <Input value={form.name} className="bg-transparent"
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="partner-product-name" />
            </div>
            <div className="sm:col-span-2">
              <Label>Description</Label>
              <Textarea rows={3} value={form.description} className="bg-transparent"
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        data-testid="partner-product-desc" />
            </div>
            <div>
              <Label>Suggested price (₹)</Label>
              <Input type="number" value={form.price} className="bg-transparent"
                     onChange={(e) => setForm({ ...form, price: e.target.value })}
                     data-testid="partner-product-price" />
            </div>
            <div>
              <Label>Stock you can supply</Label>
              <Input type="number" value={form.stock} className="bg-transparent"
                     onChange={(e) => setForm({ ...form, stock: e.target.value })}
                     data-testid="partner-product-stock" />
            </div>
            <div className="sm:col-span-2">
              <Label>Photo</Label>
              <input type="file" accept="image/*" onChange={pickPhoto} className="text-xs block mt-1.5"
                     data-testid="partner-product-photo" />
              {form.photo && (
                <img src={form.photo} alt="" className="w-20 h-20 rounded-xl object-cover border border-border mt-2" />
              )}
            </div>
          </div>
          <Button className="btn-action h-10 mt-4" disabled={saving} onClick={submit}
                  data-testid="partner-product-submit">
            {saving ? "Sending…" : "Send for review"}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-6 lg:p-8">
          <h3 className="font-display text-2xl mb-4">My products</h3>
          {products.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="partner-products-empty">
              You have not submitted a product yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="partner-products-table">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4 font-normal">Product</th>
                    <th className="py-2 pr-4 font-normal">Status</th>
                    <th className="py-2 pr-4 font-normal">Price</th>
                    <th className="py-2 pr-4 font-normal">Stock</th>
                    <th className="py-2 pr-4 font-normal">Units sold</th>
                    <th className="py-2 font-normal">Your share</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-b border-border/60" data-testid={`partner-product-${p.id}`}>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          {p.photo && (
                            <img src={mediaUrl(p.photo)} alt="" className="w-9 h-9 rounded-lg object-cover border border-border" />
                          )}
                          <span>{p.name}</span>
                        </div>
                        {p.status === "rejected" && p.rejection_reason && (
                          <div className="text-xs text-destructive mt-1">{p.rejection_reason}</div>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant="outline" className={`${STATUS_TONE[p.status]} text-[10px] capitalize`}>
                          {p.status === "pending" ? "Pending admin review" : p.status === "live" ? "Live" : "Rejected"}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap">{money(p.status === "live" ? p.live_price : p.price)}</td>
                      <td className="py-3 pr-4">{p.status === "live" ? p.live_stock : p.stock}</td>
                      <td className="py-3 pr-4">{p.units_sold || 0}</td>
                      <td className="py-3 whitespace-nowrap">
                        {p.share_pct ? `${p.share_pct}% · ${money(p.earned)}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-6 lg:p-8">
          <h3 className="font-display text-2xl mb-1">Product share earnings</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Earned when an order is paid, at the percentage agreed for that product on the day of the
            sale. A cancelled or refunded order is taken back out.
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <Figure name="This month" value={money(totals.this_month)} icon={Percent}
                    testid="share-this-month" />
            <Figure name="Lifetime accrued" value={money(totals.lifetime)} icon={IndianRupee}
                    testid="share-lifetime" />
            <Figure name="Paid to date" value={money(totals.paid)} icon={Wallet} testid="share-paid" />
            <Figure name="Pending payout" value={money(totals.pending)} icon={IndianRupee}
                    testid="share-pending" />
          </div>

          {(share.sales || []).length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="share-sales-empty">
              No sales of your products yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="share-sales-table">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4 font-normal">Sold</th>
                    <th className="py-2 pr-4 font-normal">Product</th>
                    <th className="py-2 pr-4 font-normal">Qty</th>
                    <th className="py-2 pr-4 font-normal">Item total</th>
                    <th className="py-2 pr-4 font-normal">Share</th>
                    <th className="py-2 pr-4 font-normal">You earned</th>
                    <th className="py-2 font-normal">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {share.sales.map((row) => (
                    <tr key={row.id} className="border-b border-border/60" data-testid={`share-sale-${row.id}`}>
                      <td className="py-2.5 pr-4 whitespace-nowrap text-muted-foreground">{when(row.sold_at)}</td>
                      <td className="py-2.5 pr-4">{row.product_name}</td>
                      <td className="py-2.5 pr-4">{row.quantity}</td>
                      <td className="py-2.5 pr-4 whitespace-nowrap">{money(row.base_amount)}</td>
                      <td className="py-2.5 pr-4">{row.share_pct}%</td>
                      <td className="py-2.5 pr-4 whitespace-nowrap">{money(row.amount)}</td>
                      <td className="py-2.5">
                        <Badge variant="outline" className={`text-[10px] capitalize ${
                          row.status === "reversed"
                            ? "border-destructive/40 text-destructive bg-destructive/10"
                            : "border-primary/40 text-primary bg-primary/10"}`}>
                          {row.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-border">
            <p className="overline mb-2">Product-share payments received</p>
            {(share.payouts || []).length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="share-payouts-empty">
                Nothing paid out yet.
              </p>
            ) : (
              <div className="space-y-1" data-testid="share-payouts-list">
                {share.payouts.map((entry) => (
                  <div key={entry.id} className="flex justify-between text-sm py-1.5 border-b border-border/60">
                    <span className="text-muted-foreground">{entry.paid_on} · {entry.note || "—"}</span>
                    <span>{money(entry.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerProducts;
